import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trophy, ExternalLink, RefreshCw } from "lucide-react";

// Build an iframe URL that allows editing (full edit URL with embedded chrome removed)
function toEmbedUrl(url) {
  if (!url) return "";
  const m = url.match(/spreadsheets\/d\/([^/]+)/);
  if (!m) return url;
  const sheetId = m[1];
  const gidMatch = url.match(/[?#&]gid=(\d+)/);
  const gidPart = gidMatch ? `&gid=${gidMatch[1]}` : "";
  // rm=embedded strips Google's top chrome but keeps editing capability
  return `https://docs.google.com/spreadsheets/d/${sheetId}/edit?rm=embedded${gidPart}`;
}

function toOpenUrl(url) {
  if (!url) return "";
  const m = url.match(/spreadsheets\/d\/([^/]+)/);
  if (!m) return url;
  const sheetId = m[1];
  const gidMatch = url.match(/[?#&]gid=(\d+)/);
  const tail = gidMatch ? `?gid=${gidMatch[1]}#gid=${gidMatch[1]}` : "";
  return `https://docs.google.com/spreadsheets/d/${sheetId}/edit${tail}`;
}

export default function Tournaments() {
  const [info, setInfo] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data));
  }, []);

  if (!info) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="tournaments-page">
        <p className="text-center text-[#4A4F46]">Lade …</p>
      </div>
    );
  }

  const sheetUrl = info.tournaments_sheet_url || "";
  const embedSrc = toEmbedUrl(sheetUrl);
  const openHref = toOpenUrl(sheetUrl);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="tournaments-page">
      <div className="text-center mb-10">
        <p className="label-eyebrow mb-3">Spaß &amp; Wettkampf</p>
        <h1 className="font-serif text-5xl lg:text-6xl tracking-tight" data-testid="tournaments-title">
          {info.tournaments_title || "Turniere"}
        </h1>
        {info.tournaments_intro && (
          <p className="text-[#4A4F46] mt-5 max-w-2xl mx-auto leading-relaxed whitespace-pre-line" data-testid="tournaments-intro">
            {info.tournaments_intro}
          </p>
        )}
      </div>

      {!sheetUrl ? (
        <div className="text-center py-20 border border-dashed border-[#E5DFD3] rounded-2xl">
          <Trophy className="w-10 h-10 text-[#48634A] mx-auto mb-3" />
          <p className="font-serif text-2xl">Noch keine Tabelle hinterlegt</p>
          <p className="text-[#4A4F46] mt-2 text-sm">
            Der Admin kann die Sheet-URL im Verwaltungs-Bereich eintragen.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5DFD3] rounded-2xl p-4 sm:p-6">
          {/* Mobiler Hinweis: Checkboxen funktionieren im eingebetteten Sheet auf Handy/Tablet nicht */}
          <div className="lg:hidden mb-4 bg-[#F4EFE6] border border-[#E5DFD3] rounded-xl p-4 text-center" data-testid="tournaments-mobile-hint">
            <p className="text-sm text-[#4A4F46] mb-3">
              Auf dem Smartphone oder Tablet lassen sich Häkchen in der eingebetteten Tabelle leider nicht setzen
              (Einschränkung von Google). Öffne die Tabelle dafür bitte direkt:
            </p>
            <a
              href={openHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-[#48634A] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#364A38]"
              data-testid="tournaments-mobile-open"
            >
              <ExternalLink className="w-4 h-4" /> Tabelle in Google Sheets öffnen
            </a>
          </div>
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2 text-[#4A4F46]">
              <Trophy className="w-5 h-5 text-[#48634A]" />
              <span className="text-sm">Tipp: Setze in deiner Zeile einfach ein Kreuz bei den Turnieren, an denen du teilnehmen möchtest.</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setReloadKey((k) => k + 1)}
                className="inline-flex items-center gap-1.5 text-xs text-[#4A4F46] hover:text-[#1A1C19] border border-[#E5DFD3] rounded-full px-3 py-1.5"
                data-testid="tournaments-reload"
                title="Tabelle neu laden"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Aktualisieren
              </button>
              {openHref && (
                <a
                  href={openHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-[#48634A] hover:text-[#364A38] border border-[#E5DFD3] hover:border-[#48634A] rounded-full px-3 py-1.5"
                  data-testid="tournaments-open"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> In neuem Tab öffnen
                </a>
              )}
            </div>
          </div>

          <div className="relative w-full overflow-hidden rounded-xl border border-[#E5DFD3] bg-white">
            <iframe
              key={reloadKey}
              src={embedSrc}
              title="Turnier-Anmeldungen"
              className="w-full"
              style={{ height: "min(78vh, 780px)", border: 0 }}
              loading="lazy"
              data-testid="tournaments-iframe"
            />
          </div>
          <p className="text-xs text-[#4A4F46] mt-3 italic">
            Hinweis: Damit alle Familienmitglieder eintragen können, muss das Google-Sheet auf
            &bdquo;Jeder mit dem Link &ndash; Bearbeiter&ldquo; freigegeben sein.
          </p>
        </div>
      )}
    </div>
  );
}
